import { BaseEffect } from '../BaseEffect.js';

/**
 * The Universe Within effect implementation based on Martijn Steinrucken's shader
 * Original: https://www.shadertoy.com/view/XlfGRj
 * 
 * IMPORTANT: This shader must be WebGL 1.0 compatible!
 * Rules to follow:
 * 1. No dynamic array indexing - all array indices must be constants
 * 2. No array constructors in loops
 * 3. No dynamic for loop conditions
 * 4. No array length() function
 * 5. No implicit type conversion
 */
export class UniverseEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            colorR: 0.5,           // Base color R component
            colorG: 0.8,           // Base color G component
            colorB: 1.0,           // Base color B component
            scale: 1.0,            // Scale of the effect
            intensity: 1.0,        // Overall intensity
            opacity: 0.5,          // Effect opacity
            speed: 1.0,            // Animation speed multiplier
            animate: true
        };

        super('universe', shaderManager, defaultParams);
    }

    /**
     * Initialize the effect
     */
    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision highp float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform vec2 u_mouse;
            uniform float u_opacity;
            uniform float u_speed;
            uniform float u_intensity;
            uniform float u_scale;
            uniform vec3 u_color;
            
            varying vec2 v_texcoord;

            #define S(a, b, t) smoothstep(a, b, t)
            #define NUM_LAYERS 4.

            // Helper function to create a smooth pulse
            float pulse(float t, float freq, float range) {
                return 1.0 + sin(t * freq) * range;
            }

            float N21(vec2 p) {
                vec3 a = fract(vec3(p.xyx) * vec3(213.897, 653.453, 253.098));
                a += dot(a, a.yzx + 79.76);
                return fract((a.x + a.y) * a.z);
            }

            vec2 GetPos(vec2 id, vec2 offs, float t) {
                float n = N21(id+offs);
                float n1 = fract(n*10.);
                float n2 = fract(n*100.);
                float a = t+n;
                return offs + vec2(sin(a*n1), cos(a*n2))*.4;
            }

            float df_line(in vec2 a, in vec2 b, in vec2 p) {
                vec2 pa = p - a, ba = b - a;
                float h = clamp(dot(pa,ba) / dot(ba,ba), 0., 1.);    
                return length(pa - ba * h);
            }

            float line(vec2 a, vec2 b, vec2 uv) {
                float r1 = .04;
                float r2 = .01;
                
                float d = df_line(a, b, uv);
                float d2 = length(a-b);
                float fade = S(1.5, .5, d2);
                
                fade += S(.05, .02, abs(d2-.75));
                return S(r1, r2, d)*fade;
            }

            float NetLayer(vec2 st, float n, float t) {
                vec2 id = floor(st)+n;
                st = fract(st)-.5;
               
                // Calculate grid points
                vec2 p0 = GetPos(id, vec2(-1.,-1.), t);
                vec2 p1 = GetPos(id, vec2( 0.,-1.), t);
                vec2 p2 = GetPos(id, vec2( 1.,-1.), t);
                vec2 p3 = GetPos(id, vec2(-1., 0.), t);
                vec2 p4 = GetPos(id, vec2( 0., 0.), t);
                vec2 p5 = GetPos(id, vec2( 1., 0.), t);
                vec2 p6 = GetPos(id, vec2(-1., 1.), t);
                vec2 p7 = GetPos(id, vec2( 0., 1.), t);
                vec2 p8 = GetPos(id, vec2( 1., 1.), t);
                
                float m = 0.;
                float sparkle = 0.;
                
                // Connect center point (p4) with all other points
                m += line(p4, p0, st);
                m += line(p4, p1, st);
                m += line(p4, p2, st);
                m += line(p4, p3, st);
                m += line(p4, p5, st);
                m += line(p4, p6, st);
                m += line(p4, p7, st);
                m += line(p4, p8, st);
                
                // Calculate sparkles for each point
                float d;
                float s;
                float pulse;
                
                d = length(st-p0);
                s = (.005/(d*d));
                s *= S(1., .7, d);
                pulse = sin((fract(p0.x)+fract(p0.y)+t)*5.)*.4+.6;
                pulse = pow(pulse, 20.);
                s *= pulse;
                sparkle += s;
                
                d = length(st-p1);
                s = (.005/(d*d));
                s *= S(1., .7, d);
                pulse = sin((fract(p1.x)+fract(p1.y)+t)*5.)*.4+.6;
                pulse = pow(pulse, 20.);
                s *= pulse;
                sparkle += s;
                
                d = length(st-p2);
                s = (.005/(d*d));
                s *= S(1., .7, d);
                pulse = sin((fract(p2.x)+fract(p2.y)+t)*5.)*.4+.6;
                pulse = pow(pulse, 20.);
                s *= pulse;
                sparkle += s;
                
                d = length(st-p3);
                s = (.005/(d*d));
                s *= S(1., .7, d);
                pulse = sin((fract(p3.x)+fract(p3.y)+t)*5.)*.4+.6;
                pulse = pow(pulse, 20.);
                s *= pulse;
                sparkle += s;
                
                d = length(st-p4);
                s = (.005/(d*d));
                s *= S(1., .7, d);
                pulse = sin((fract(p4.x)+fract(p4.y)+t)*5.)*.4+.6;
                pulse = pow(pulse, 20.);
                s *= pulse;
                sparkle += s;
                
                d = length(st-p5);
                s = (.005/(d*d));
                s *= S(1., .7, d);
                pulse = sin((fract(p5.x)+fract(p5.y)+t)*5.)*.4+.6;
                pulse = pow(pulse, 20.);
                s *= pulse;
                sparkle += s;
                
                d = length(st-p6);
                s = (.005/(d*d));
                s *= S(1., .7, d);
                pulse = sin((fract(p6.x)+fract(p6.y)+t)*5.)*.4+.6;
                pulse = pow(pulse, 20.);
                s *= pulse;
                sparkle += s;
                
                d = length(st-p7);
                s = (.005/(d*d));
                s *= S(1., .7, d);
                pulse = sin((fract(p7.x)+fract(p7.y)+t)*5.)*.4+.6;
                pulse = pow(pulse, 20.);
                s *= pulse;
                sparkle += s;
                
                d = length(st-p8);
                s = (.005/(d*d));
                s *= S(1., .7, d);
                pulse = sin((fract(p8.x)+fract(p8.y)+t)*5.)*.4+.6;
                pulse = pow(pulse, 20.);
                s *= pulse;
                sparkle += s;
                
                // Add additional connections
                m += line(p1, p3, st);
                m += line(p1, p5, st);
                m += line(p7, p5, st);
                m += line(p7, p3, st);
                
                float sPhase = (sin(t+n)+sin(t*.1))*.25+.5;
                sPhase += pow(sin(t*.1)*.5+.5, 50.)*5.;
                m += sparkle*sPhase;
                
                return m;
            }

            void main() {
                vec2 uv = (gl_FragCoord.xy-u_resolution.xy*.5)/u_resolution.y;
                vec2 M = (u_mouse.xy/u_resolution.xy-.5) * 2.0;
                
                // Base time for rotations and colors
                float t = u_time * 0.1;
                float speedTime = u_time * u_speed;
                
                // Create pulsing variations based on speed
                float intensityPulse = pulse(speedTime, 2.0, 0.2);  // Pulse intensity ±20%
                float scalePulse = pulse(speedTime, 3.0, 0.15);     // Pulse scale ±15%
                float colorPulse = pulse(speedTime, 4.0, 0.1);      // Pulse color ±10%
                
                float s = sin(t);
                float c = cos(t);
                mat2 rot = mat2(c, -s, s, c);
                vec2 st = uv * rot * (u_scale * scalePulse);  // Apply scale pulse
                M *= rot*2.;
                
                float m = 0.;
                for(float i=0.; i<1.; i+=1./NUM_LAYERS) {
                    float z = fract(t+i);
                    float size = mix(15., 1., z);
                    float fade = S(0., .6, z)*S(1., .8, z);
                    
                    // Apply speed to the grid movement
                    vec2 offset = st*size-M*z;
                    offset.y += speedTime * 0.5;  // Move grid based on speed
                    m += fade * NetLayer(offset, i, t);
                }
                
                vec3 baseCol = vec3(s, cos(t*.4), -sin(t*.24))*.4+.6;
                baseCol *= u_color * colorPulse;  // Apply color pulse
                vec3 col = baseCol * m * (u_intensity * intensityPulse);  // Apply intensity pulse
                col += baseCol * (-uv.y * 0.5);
                
                col *= 1.-dot(uv,uv);
                
                vec4 originalColor = texture2D(u_image, v_texcoord);
                gl_FragColor = mix(originalColor, vec4(col, 1.0), u_opacity);
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    /**
     * Set up effect-specific uniforms
     */
    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_color', { type: '3fv' });
        this.addUniform('u_scale', { type: '1f' });
        this.addUniform('u_intensity', { type: '1f' });
        this.addUniform('u_opacity', { type: '1f' });
        this.addUniform('u_speed', { type: '1f' });
        this.addUniform('u_mouse', { type: '2f' });
    }

    /**
     * Update effect-specific uniforms
     * @param {Object} uniforms - Uniform values to update
     */
    updateUniforms(uniforms) {
        Object.assign(uniforms, {
            u_color: { 
                value: [this.params.colorR, this.params.colorG, this.params.colorB],
                type: '3fv'
            },
            u_scale: { value: this.params.scale, type: '1f' },
            u_intensity: { value: this.params.intensity, type: '1f' },
            u_opacity: { value: this.params.opacity, type: '1f' },
            u_speed: { value: this.params.speed, type: '1f' },
            u_mouse: { value: [0, 0], type: '2f' }  // Will be updated by mouse movement
        });

        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    /**
     * Get UI configuration for parameters
     * @returns {Object} UI configuration
     */
    getUIConfig() {
        return {
            opacity: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Opacity'
            },
            speed: {
                min: 0,
                max: 5.0,
                step: 0.1,
                label: 'Speed'
            },
            scale: {
                min: 0.1,
                max: 5.0,
                step: 0.1,
                label: 'Scale'
            },
            intensity: {
                min: 0,
                max: 5.0,
                step: 0.1,
                label: 'Intensity'
            },
            colorR: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Color R'
            },
            colorG: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Color G'
            },
            colorB: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Color B'
            },
            animate: {
                label: 'Animate'
            }
        };
    }
} 